import React, { useState, useEffect, useRef } from 'react';
import AICharactersIllustration from './AICharactersIllustration';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, 
  Briefcase, 
  Rocket, 
  ArrowRight,
  Check,
  ChevronRight,
  Sparkles,
  ExternalLink,
  Brain,
  Zap,
  TrendingUp,
  MessageCircle,
  Repeat2,
  Heart,
  Share,
  MoreHorizontal,
  RefreshCw,
  Filter,
  Radio,
  Construction,
  Wrench,
  Cog,
  X
} from 'lucide-react';

// ============= NAVBAR =============
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/40 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <motion.a 
            href="#home"
            className="flex items-center gap-3 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="relative">
              <motion.img 
                src="/logo.png" 
                alt="AI Factory Logo" 
                className="h-10 w-10 object-contain rounded-lg"
                animate={{ 
                  filter: ['drop-shadow(0 0 8px rgba(20, 184, 166, 0.3))', 'drop-shadow(0 0 12px rgba(20, 184, 166, 0.5))', 'drop-shadow(0 0 8px rgba(20, 184, 166, 0.3))']
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div 
                className="absolute -inset-1 rounded-xl bg-gradient-to-r from-teal-500/0 via-teal-500/20 to-emerald-500/0 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-300"
              />
            </div>
            
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-tight text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-300">
                AI Factory
              </span>
              <span className="text-[10px] text-gray-500 tracking-widest uppercase hidden sm:block">
                Transform with AI
              </span>
            </div>
          </motion.a>

          <div className="hidden md:flex items-center gap-12">
            <a href="#home" className="text-sm text-gray-300 hover:text-white transition">Home</a>
            <a href="#offerings" className="text-sm text-gray-300 hover:text-white transition">Our Offerings</a>
            <a href="#about" className="text-sm text-gray-300 hover:text-white transition">About Us</a>
          </div>

          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Contact Us
          </motion.a>

          <button 
            className="md:hidden flex flex-col gap-1.5"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span className="w-6 h-0.5 bg-white"></span>
            <span className="w-6 h-0.5 bg-white"></span>
            <span className="w-6 h-0.5 bg-white"></span>
          </button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= UNDER DEVELOPMENT MODAL =============
const UnderDevelopmentModal = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-slate-950/90 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal Content */}
          <motion.div
            className="relative w-full max-w-md overflow-hidden"
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Animated Background Glow */}
            <motion.div
              className="absolute -inset-4 bg-gradient-to-r from-orange-500/30 via-amber-500/30 to-yellow-500/30 rounded-[40px] blur-2xl"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Card */}
            <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-white/10 rounded-3xl p-8 overflow-hidden">
              {/* Animated Grid Pattern */}
              <div 
                className="absolute inset-0 opacity-[0.03]"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(251, 191, 36, 0.5) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(251, 191, 36, 0.5) 1px, transparent 1px)
                  `,
                  backgroundSize: '30px 30px'
                }}
              />

              {/* Floating Particles */}
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1.5 h-1.5 rounded-full bg-amber-400/40"
                  style={{
                    left: `${10 + Math.random() * 80}%`,
                    top: `${10 + Math.random() * 80}%`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    x: [0, Math.random() * 10 - 5, 0],
                    opacity: [0.2, 0.6, 0.2],
                    scale: [1, 1.5, 1],
                  }}
                  transition={{
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}

              {/* Close Button */}
              <motion.button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all z-10"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <X size={20} />
              </motion.button>

              {/* Content */}
              <div className="relative z-10 text-center">
                {/* Animated Icon Group */}
                <div className="relative w-32 h-32 mx-auto mb-6">
                  {/* Outer Ring */}
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-dashed border-amber-500/30"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  />
                  
                  {/* Middle Ring */}
                  <motion.div
                    className="absolute inset-3 rounded-full border-2 border-amber-500/20"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                  />

                  {/* Inner Glow */}
                  <motion.div
                    className="absolute inset-6 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/20"
                    animate={{
                      scale: [1, 1.1, 1],
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />

                  {/* Central Icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      className="relative"
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      {/* Rocket Icon */}
                      <motion.div
                        className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30"
                        animate={{
                          boxShadow: [
                            '0 10px 30px -10px rgba(249, 115, 22, 0.3)',
                            '0 10px 40px -10px rgba(249, 115, 22, 0.5)',
                            '0 10px 30px -10px rgba(249, 115, 22, 0.3)',
                          ]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <Rocket className="w-8 h-8 text-white" />
                      </motion.div>

                      {/* Orbiting Elements */}
                      <motion.div
                        className="absolute -top-2 -right-2"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                        style={{ transformOrigin: "24px 24px" }}
                      >
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                          <Cog className="w-3.5 h-3.5 text-white" />
                        </div>
                      </motion.div>

                      <motion.div
                        className="absolute -bottom-1 -left-3"
                        animate={{ rotate: -360 }}
                        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                        style={{ transformOrigin: "20px -8px" }}
                      >
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                          <Wrench className="w-2.5 h-2.5 text-white" />
                        </div>
                      </motion.div>
                    </motion.div>
                  </div>
                </div>

                {/* Badge */}
                <motion.div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 mb-4"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                >
                  <motion.div
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Construction className="w-4 h-4 text-orange-400" />
                  </motion.div>
                  <span className="text-sm font-semibold text-orange-300">Work in Progress</span>
                </motion.div>

                {/* Title */}
                <motion.h3
                  className="text-2xl font-bold mb-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent">
                    Under Development
                  </span>
                </motion.h3>

                {/* Description */}
                <motion.p
                  className="text-gray-400 mb-6 leading-relaxed"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  Our Product Development service is being crafted with care. We're building something amazing to help you create cutting-edge AI products.
                </motion.p>

                {/* Progress Indicator */}
                <motion.div
                  className="mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                    <span>Progress</span>
                    <span className="text-amber-400 font-semibold">Coming Soon</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: '65%' }}
                      transition={{ delay: 0.6, duration: 1, ease: "easeOut" }}
                    />
                  </div>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  className="flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <motion.a
                    href="#contact"
                    onClick={onClose}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl text-white font-semibold text-sm hover:shadow-lg hover:shadow-orange-500/30 transition-all flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Sparkles size={16} />
                    Get Notified
                  </motion.a>
                  <motion.button
                    onClick={onClose}
                    className="flex-1 px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-300 font-semibold text-sm hover:bg-white/10 transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Explore Other Services
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============= ACTIVE NEURAL NETWORK BACKGROUND =============
const NeuralNetworkBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let nodes = [];
    let packets = [];
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initNodes();
    };
    
    const initNodes = () => {
      nodes = [];
      const nodeCount = Math.floor((canvas.width * canvas.height) / 20000);
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      for (let i = 0; i < nodeCount; i++) {
        let x = Math.random() * canvas.width;
        let y = Math.random() * canvas.height;
        const distFromCenter = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
        const maxDist = Math.sqrt(Math.pow(centerX, 2) + Math.pow(centerY, 2));
        const edgeFactor = Math.min(distFromCenter / (maxDist * 0.5), 1);
        
        nodes.push({
          x, y,
          vx: (Math.random() - 0.5) * 0.7,
          vy: (Math.random() - 0.5) * 0.7,
          baseRadius: (Math.random() * 1.5 + 1) * (0.6 + edgeFactor * 0.8),
          pulsePhase: Math.random() * Math.PI * 2,
          pulseSpeed: 0.018 + Math.random() * 0.02,
          type: Math.random() > 0.7 ? 'key' : 'normal',
          edgeFactor
        });
      }
    };
    
    const animate = () => {
      ctx.fillStyle = 'rgba(6, 11, 20, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const maxDist = Math.sqrt(Math.pow(centerX, 2) + Math.pow(centerY, 2));
      
      nodes.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        node.pulsePhase += node.pulseSpeed;
        
        const distFromCenter = Math.sqrt(Math.pow(node.x - centerX, 2) + Math.pow(node.y - centerY, 2));
        node.edgeFactor = Math.min(distFromCenter / (maxDist * 0.4), 1);
        
        if (node.x < 15 || node.x > canvas.width - 15) node.vx *= -1;
        if (node.y < 15 || node.y > canvas.height - 15) node.vy *= -1;
        node.x = Math.max(15, Math.min(canvas.width - 15, node.x));
        node.y = Math.max(15, Math.min(canvas.height - 15, node.y));
        
        nodes.forEach((otherNode, j) => {
          if (i >= j) return;
          const dx = node.x - otherNode.x;
          const dy = node.y - otherNode.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 170) {
            const distOpacity = (1 - distance / 170);
            const positionOpacity = (node.edgeFactor + otherNode.edgeFactor) / 2;
            const opacity = distOpacity * 0.35 * (0.3 + positionOpacity * 0.7);
            
            const gradient = ctx.createLinearGradient(node.x, node.y, otherNode.x, otherNode.y);
            gradient.addColorStop(0, `rgba(20, 184, 166, ${opacity})`);
            gradient.addColorStop(0.5, `rgba(52, 211, 153, ${opacity * 0.7})`);
            gradient.addColorStop(1, `rgba(20, 184, 166, ${opacity})`);
            
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 1 + opacity;
            ctx.stroke();
            
            if (Math.random() > 0.995 && positionOpacity > 0.5) {
              packets.push({
                startX: node.x, startY: node.y,
                endX: otherNode.x, endY: otherNode.y,
                progress: 0, speed: 0.018 + Math.random() * 0.025,
                size: 1.5 + Math.random() * 2
              });
            }
          }
        });
        
        const pulse = Math.sin(node.pulsePhase) * 0.5 + 0.5;
        const baseOpacity = node.type === 'key' ? 0.5 : 0.3;
        const nodeOpacity = (baseOpacity + pulse * 0.25) * (0.35 + node.edgeFactor * 0.65);
        const nodeRadius = node.baseRadius * (1 + pulse * 0.25);
        
        if (node.type === 'key' && node.edgeFactor > 0.3) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius * 4, 0, Math.PI * 2);
          const glowGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, nodeRadius * 4);
          glowGradient.addColorStop(0, `rgba(20, 184, 166, ${nodeOpacity * 0.4})`);
          glowGradient.addColorStop(0.5, `rgba(20, 184, 166, ${nodeOpacity * 0.15})`);
          glowGradient.addColorStop(1, 'rgba(20, 184, 166, 0)');
          ctx.fillStyle = glowGradient;
          ctx.fill();
        }
        
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius, 0, Math.PI * 2);
        ctx.fillStyle = node.type === 'key' ? `rgba(94, 234, 212, ${nodeOpacity})` : `rgba(20, 184, 166, ${nodeOpacity})`;
        ctx.fill();
        
        if (node.type === 'key' && node.edgeFactor > 0.5) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${nodeOpacity * 0.7})`;
          ctx.fill();
        }
      });
      
      packets = packets.filter(packet => {
        packet.progress += packet.speed;
        if (packet.progress >= 1) return false;
        
        const x = packet.startX + (packet.endX - packet.startX) * packet.progress;
        const y = packet.startY + (packet.endY - packet.startY) * packet.progress;
        
        ctx.beginPath();
        ctx.arc(x, y, packet.size * 2.5, 0, Math.PI * 2);
        const packetGlow = ctx.createRadialGradient(x, y, 0, x, y, packet.size * 2.5);
        packetGlow.addColorStop(0, 'rgba(52, 211, 153, 0.6)');
        packetGlow.addColorStop(0.6, 'rgba(52, 211, 153, 0.2)');
        packetGlow.addColorStop(1, 'rgba(52, 211, 153, 0)');
        ctx.fillStyle = packetGlow;
        ctx.fill();
        
        ctx.beginPath();
        ctx.arc(x, y, packet.size, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(167, 243, 208, 0.85)';
        ctx.fill();
        
        return true;
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    resize();
    window.addEventListener('resize', resize);
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);
  
  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ opacity: 0.7 }} />;
};

// ============= FLOWING ATTENTION BEAMS =============
const AttentionBeams = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(5)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute h-px bg-gradient-to-r from-transparent via-teal-400/30 to-transparent"
        style={{ width: '220%', left: '-60%', top: `${12 + i * 18}%`, transform: `rotate(${-12 + (i % 2) * 6}deg)` }}
        animate={{ x: ['-35%', '35%'], opacity: [0, 0.5, 0] }}
        transition={{ duration: 4.5 + i * 0.4, repeat: Infinity, delay: i * 0.7, ease: "linear" }}
      />
    ))}
    {[0, 1].map((i) => (
      <motion.div
        key={`scan-${i}`}
        className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-emerald-400/25 to-transparent"
        animate={{ x: ['-5vw', '105vw'] }}
        transition={{ duration: 8, repeat: Infinity, delay: i * 4, ease: "linear" }}
      />
    ))}
    <motion.div
      className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-300/20 to-transparent"
      style={{ top: '50%' }}
      animate={{ opacity: [0, 0.4, 0], scaleY: [1, 1.5, 1] }}
      transition={{ duration: 3, repeat: Infinity }}
    />
  </div>
);

// ============= HERO SECTION =============
const Hero = () => {
  const words = ["Training", "Services", "Products", "Innovation"];
  const [currentWord, setCurrentWord] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => setCurrentWord((prev) => (prev + 1) % words.length), 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-[#060b14]" />
      <NeuralNetworkBackground />
      <AttentionBeams />
      
      <motion.div className="absolute top-1/4 left-1/5 w-[550px] h-[550px] rounded-full blur-[140px] bg-teal-500/20"
        animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div className="absolute bottom-1/4 right-1/5 w-[450px] h-[450px] rounded-full blur-[130px] bg-emerald-500/15"
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.12, 0.2, 0.12] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 45%, rgba(6, 11, 20, 0.4) 0%, transparent 70%)' }} />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(20,184,166,0.15),transparent)]" />

      <div className="relative z-10 text-center max-w-5xl mx-auto px-4 pt-20 pb-8">
        <motion.a href="https://arxiv.org/abs/1706.03762" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 mb-6"
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-white/[0.03] border border-teal-500/30 backdrop-blur-sm hover:bg-teal-500/10 hover:border-teal-400/50 transition-all duration-300">
            <div className="relative">
              <motion.div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center"
                animate={{ rotate: [0, 360] }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
                <Brain className="w-3.5 h-3.5 text-white" />
              </motion.div>
              <motion.div className="absolute inset-0 rounded-full border border-teal-400/50"
                animate={{ scale: [1, 1.5], opacity: [0.5, 0] }} transition={{ duration: 1.5, repeat: Infinity }} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Built on</span>
              <span className="text-sm font-semibold text-teal-300 group-hover:text-teal-200 transition">"Attention Is All You Need"</span>
              <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-teal-400 transition" />
            </div>
          </div>
        </motion.a>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.1] mb-4">
            <span className="text-white">AI</span>
            <span className="relative inline-block ml-4">
              <span className="bg-gradient-to-r from-teal-400 via-emerald-400 to-teal-300 bg-clip-text text-transparent">Factory</span>
              <motion.div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-emerald-400 to-teal-500 rounded-full"
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.8 }} />
            </span>
          </h1>
        </motion.div>

        <motion.div className="text-xl md:text-2xl text-gray-300 mb-8 h-8 flex items-center justify-center gap-2"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          <span>Where Transformers Power Your AI</span>
          <AnimatePresence mode="wait">
            <motion.span key={currentWord} className="text-teal-400 font-semibold inline-block min-w-[140px]"
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }} transition={{ duration: 0.3 }}>
              {words[currentWord]}
            </motion.span>
          </AnimatePresence>
        </motion.div>

        <motion.p className="text-base md:text-lg text-gray-400 mb-8 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          Join <span className="text-white font-semibold">5,000+</span> professionals mastering AI through hands-on training and real-world projects.{' '}
          <span className="text-teal-400">Transform your career in months, not years.</span>
        </motion.p>

        <motion.div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
          initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="group relative px-8 py-4 rounded-full text-white font-semibold overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-600 via-emerald-500 to-teal-500" />
            <motion.div className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400"
              initial={{ x: '-100%' }} whileHover={{ x: '0%' }} transition={{ duration: 0.3 }} />
            <a href="#offerings" className="relative z-10 flex items-center gap-2">
              Explore Programs <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.button>
          
          <motion.a href="https://wa.me/919968284344?text=Hi%20I%20am%20interested%20in%20a%20free%20consultation.%20Please%20connect%20with%20me."
            whileHover={{ scale: 1.03, borderColor: 'rgba(20, 184, 166, 0.5)' }} whileTap={{ scale: 0.97 }}
            className="px-8 py-4 border border-white/15 rounded-full text-white font-semibold hover:bg-white/5 transition-all flex items-center gap-2 backdrop-blur-sm">
            <Sparkles size={18} className="text-teal-400" />Free Consultation
          </motion.a>
        </motion.div>

        <motion.div className="grid grid-cols-3 gap-8 max-w-xl mx-auto"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}>
          {[{ value: '5,000+', label: 'Professionals Trained' }, { value: '50+', label: 'Expert Mentors' }, { value: '95%', label: 'Career Success Rate' }].map((stat, i) => (
            <motion.div key={i} className="text-center" whileHover={{ scale: 1.05 }}>
              <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">{stat.value}</div>
              <div className="text-[10px] md:text-xs text-gray-500 mt-1 uppercase tracking-wider">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div className="absolute bottom-6 left-1/2 -translate-x-1/2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
        <motion.div className="w-5 h-8 border border-white/20 rounded-full flex justify-center pt-1.5" animate={{ y: [0, 4, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <motion.div className="w-1 h-1.5 bg-teal-400 rounded-full" animate={{ y: [0, 6, 0], opacity: [1, 0.3, 1] }} transition={{ duration: 2, repeat: Infinity }} />
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= OFFERING CARD =============
const OfferingCard = ({ offering, index, onUnderDevelopmentClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const icons = { 'graduation': GraduationCap, 'briefcase': Briefcase, 'rocket': Rocket };
  const Icon = icons[offering.icon];

  const handleClick = (e) => {
    if (offering.underDevelopment) {
      e.preventDefault();
      onUnderDevelopmentClick();
    }
  };

  return (
    <motion.div className="relative group h-full"
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 184, 166, 0.3) 1px, transparent 1px)`, backgroundSize: '20px 20px' }}></div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-500/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        {offering.underDevelopment && (
          <motion.div className="absolute top-4 right-4 z-20"
            initial={{ scale: 0, rotate: -12 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.3 + index * 0.1, type: "spring" }}>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/40">
              <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                <Construction size={12} className="text-orange-400" />
              </motion.div>
              <span className="text-[10px] font-bold text-orange-300 uppercase tracking-wider">Soon</span>
            </div>
          </motion.div>
        )}

        <div className="relative z-10 flex flex-col h-full">
          <motion.div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all duration-500"
            animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}} transition={{ duration: 0.5 }}>
            <Icon className="text-teal-300 group-hover:text-teal-200 transition" size={32} />
          </motion.div>

          <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-300">{offering.title}</h3>
          <p className="text-gray-400 mb-6 flex-grow leading-relaxed">{offering.description}</p>

          <ul className="space-y-3 mb-8">
            {offering.features.map((feature, i) => (
              <motion.li key={i} className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * i }}>
                <Check size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" /><span>{feature}</span>
              </motion.li>
            ))}
          </ul>

          <motion.a href={offering.link} onClick={handleClick} whileHover={{ x: 4 }}
            className={`inline-flex items-center gap-2 transition font-semibold text-sm mt-auto ${offering.underDevelopment ? 'text-orange-400 hover:text-orange-300 cursor-pointer' : 'text-teal-400 hover:text-teal-300'}`}>
            {offering.underDevelopment ? 'Coming Soon' : 'Learn More'} <ChevronRight size={16} />
          </motion.a>
        </div>
      </div>
    </motion.div>
  );
};

// ============= OFFERINGS SECTION =============
const OfferingsSection = () => {
  const [showUnderDevModal, setShowUnderDevModal] = useState(false);

  const offerings = [
    { icon: 'graduation', title: "Training", description: "Master AI skills through expert-led training programs. From foundational concepts to advanced implementations, upskill yourself and your team.",
      features: ["Beginner to Advanced Courses", "Live Interactive Sessions", "Real-World Case Studies", "Industry Certifications"], link: "/training", underDevelopment: false },
    { icon: 'briefcase', title: "Services", description: "End-to-end AI solutions for businesses. From consulting to implementation, we help organizations transform with artificial intelligence.",
      features: ["AI Strategy & Consulting", "Custom Model Development", "Integration & Deployment", "Maintenance & Support"], link: "/services", underDevelopment: false },
    { icon: 'rocket', title: "Product Development", description: "Build cutting-edge AI products from concept to launch. Full-stack development expertise for AI-powered applications and platforms.",
      features: ["MVP Development", "Scalable Architecture", "UI/UX Design Integration", "Launch & Scale Support"], link: "#contact", underDevelopment: true }
  ];

  return (
    <section id="offerings" className="relative py-24 px-4 overflow-hidden bg-slate-950/40">
      <div className="max-w-7xl mx-auto">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">What We Offer</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Offerings</h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">Comprehensive solutions to accelerate your AI journey - from training to deployment</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {offerings.map((offering, index) => (
            <OfferingCard key={index} offering={offering} index={index} onUnderDevelopmentClick={() => setShowUnderDevModal(true)} />
          ))}
        </div>
      </div>
      <UnderDevelopmentModal isOpen={showUnderDevModal} onClose={() => setShowUnderDevModal(false)} />
    </section>
  );
};

// ============= ABOUT SECTION =============
const AboutSection = () => {
  const features = ["Industry-Aligned Curriculum", "Live Project Experience", "One-on-One Mentorship", "Lifetime Career Support", "Flexible Learning Options"];

  return (
    <section id="about" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">About Us</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why AI Factory</h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">Transforming Careers</h3>
            <p className="text-gray-300 mb-6 leading-relaxed">At AI Factory, we're committed to making AI education accessible and effective. Our programs are designed by industry experts and continuously updated to reflect the latest developments in artificial intelligence.</p>
            <p className="text-gray-300 mb-8 leading-relaxed">We believe in hands-on learning, real-world projects, and personalized mentorship. Our graduates don't just learn theory—they build production-ready AI systems.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div key={index} className="flex items-center gap-3 text-gray-300"
                  initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: index * 0.1 }} viewport={{ once: true }}>
                  <ArrowRight size={16} className="text-teal-400 flex-shrink-0" /><span>{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div className="flex items-center justify-center" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <motion.div className="text-[12rem] opacity-80" animate={{ y: [0, -20, 0], rotate: [-5, 5, -5] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
              <AICharactersIllustration />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============= AI FEED SECTION =============
const AIFeedSection = () => {
  const [tweets, setTweets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hoveredTweet, setHoveredTweet] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const filters = [
    { id: 'all', label: 'All', icon: Zap, color: 'from-violet-500 to-purple-500' },
    { id: 'ai', label: 'AI', icon: Brain, color: 'from-blue-500 to-cyan-500' },
    { id: 'ml', label: 'ML', icon: TrendingUp, color: 'from-teal-500 to-emerald-500' },
    { id: 'llm', label: 'LLMs', icon: MessageCircle, color: 'from-orange-500 to-amber-500' },
    { id: 'datascience', label: 'Data Science', icon: Filter, color: 'from-pink-500 to-rose-500' },
  ];

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const fetchTweets = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tweets`);
      if (!response.ok) throw new Error('Failed to fetch tweets');
      const data = await response.json();
      setTweets(data.tweets || []);
      setLastUpdated(new Date(data.lastUpdated));
    } catch (err) {
      console.error('Error fetching tweets:', err);
      setTweets(getMockTweets());
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTweets();
    const interval = setInterval(() => fetchTweets(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getMockTweets = () => [
    { id: '1', author: { name: 'Andrej Karpathy', handle: '@karpathy', avatar: null, verified: true }, content: 'The most important thing about neural networks is not the architecture, but the data. Always start with data quality.', timestamp: '2h', likes: 4523, retweets: 892, tags: ['ai', 'ml'], url: 'https://twitter.com/karpathy' },
    { id: '2', author: { name: 'OpenAI', handle: '@OpenAI', avatar: null, verified: true }, content: 'Introducing new capabilities: enhanced reasoning, better code generation, and multimodal understanding.', timestamp: '4h', likes: 45231, retweets: 12453, tags: ['ai', 'llm'], url: 'https://twitter.com/OpenAI' },
    { id: '3', author: { name: 'Hugging Face', handle: '@huggingface', avatar: null, verified: true }, content: '🚀 New open-source LLM just dropped! 70B parameters, MIT license, outperforms GPT-4 on reasoning benchmarks.', timestamp: '6h', likes: 12453, retweets: 4521, tags: ['ai', 'llm'], url: 'https://twitter.com/huggingface' },
  ];

  const filteredTweets = activeFilter === 'all' ? tweets : tweets.filter(tweet => tweet.tags?.includes(activeFilter));
  const formatNumber = (num) => num >= 1000000 ? (num / 1000000).toFixed(1) + 'M' : num >= 1000 ? (num / 1000).toFixed(1) + 'K' : num;
  const getTagColor = (tag) => {
    const colors = { ai: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-400', ml: 'from-teal-500/20 to-emerald-500/20 border-teal-500/30 text-teal-400', llm: 'from-orange-500/20 to-amber-500/20 border-orange-500/30 text-orange-400', datascience: 'from-pink-500/20 to-rose-500/20 border-pink-500/30 text-pink-400' };
    return colors[tag] || 'from-gray-500/20 to-gray-500/20 border-gray-500/30 text-gray-400';
  };

  return (
    <section id="ai-feed" className="relative py-24 px-4 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#060b14]" />
        <motion.div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-violet-600/20 to-purple-600/10 rounded-full blur-3xl"
          animate={{ x: [0, 50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }} transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute top-1/2 right-10 w-96 h-96 bg-gradient-to-br from-teal-600/15 to-emerald-600/10 rounded-full blur-3xl"
          animate={{ x: [0, -40, 0], y: [0, -50, 0], scale: [1.1, 1, 1.1] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 184, 166, 0.5) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <motion.div className="relative" animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}>
              <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-red-500/20 via-orange-500/20 to-amber-500/20 border border-red-500/30 backdrop-blur-sm">
                <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span></span>
                <span className="text-sm font-bold text-red-400 uppercase tracking-wider">Live</span>
              </div>
            </motion.div>
            <motion.button onClick={() => fetchTweets(true)} disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:border-teal-500/30 hover:bg-teal-500/10 transition-all text-gray-400 hover:text-teal-400"
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} /><span className="text-xs font-medium">Refresh</span>
            </motion.button>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-4"><span className="text-white">AI </span><span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">Pulse</span></h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg mb-2">Real-time insights from the minds shaping artificial intelligence</p>
          {lastUpdated && <motion.p className="text-gray-600 text-sm flex items-center justify-center gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><Radio size={12} className="text-teal-500" />Updated {lastUpdated.toLocaleTimeString()}</motion.p>}
        </motion.div>

        <motion.div className="flex flex-wrap justify-center gap-3 mb-12" initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }} viewport={{ once: true }}>
          {filters.map((filter, index) => {
            const FilterIcon = filter.icon;
            const isActive = activeFilter === filter.id;
            return (
              <motion.button key={filter.id} onClick={() => setActiveFilter(filter.id)}
                className={`relative group px-5 py-2.5 rounded-2xl text-sm font-semibold transition-all duration-300 overflow-hidden ${isActive ? 'text-white shadow-lg' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10 hover:border-white/20'}`}
                whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                {isActive && <motion.div className={`absolute inset-0 bg-gradient-to-r ${filter.color}`} layoutId="activeFilter" transition={{ type: "spring", bounce: 0.2, duration: 0.6 }} />}
                <div className={`absolute inset-0 bg-gradient-to-r ${filter.color} opacity-0 group-hover:opacity-20 transition-opacity rounded-2xl`} />
                <span className="relative z-10 flex items-center gap-2"><FilterIcon size={14} />{filter.label}</span>
              </motion.button>
            );
          })}
        </motion.div>

        {loading ? (
          <div className="flex flex-col justify-center items-center py-20 gap-4">
            <div className="relative">
              <motion.div className="w-16 h-16 rounded-full border-2 border-teal-500/20" animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} />
              <motion.div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-transparent border-t-teal-500" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
            </div>
            <p className="text-gray-500 text-sm">Loading AI insights...</p>
          </div>
        ) : (
          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <AnimatePresence mode="popLayout">
              {filteredTweets.map((tweet, index) => (
                <motion.a key={tweet.id} href={tweet.url} target="_blank" rel="noopener noreferrer" layout
                  initial={{ opacity: 0, scale: 0.8, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }} className="group block"
                  onMouseEnter={() => setHoveredTweet(tweet.id)} onMouseLeave={() => setHoveredTweet(null)}>
                  <motion.div className="relative h-full overflow-hidden" whileHover={{ y: -8 }} transition={{ duration: 0.3 }}>
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 via-transparent to-purple-500/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
                    <div className="relative bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-6 h-full transition-all duration-500 group-hover:border-teal-500/40">
                      <div className="relative z-10">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">{tweet.author.name.charAt(0)}</div>
                            <div><span className="font-bold text-white text-sm group-hover:text-teal-300 transition-colors">{tweet.author.name}</span><br/><span className="text-gray-500 text-xs">{tweet.author.handle}</span></div>
                          </div>
                          <span className="text-gray-500 text-xs bg-white/5 px-2 py-1 rounded-full">{tweet.timestamp}</span>
                        </div>
                        <p className="text-gray-200 text-sm leading-relaxed mb-5 line-clamp-4 group-hover:text-white transition-colors">{tweet.content}</p>
                        <div className="flex flex-wrap gap-2 mb-5">
                          {tweet.tags?.map((tag) => (<motion.span key={tag} className={`px-3 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r ${getTagColor(tag)} border backdrop-blur-sm`} whileHover={{ scale: 1.1 }}>#{tag}</motion.span>))}
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-white/5">
                          <div className="flex items-center gap-5">
                            <motion.div className="flex items-center gap-2 text-gray-500 hover:text-pink-500 transition-colors cursor-pointer" whileHover={{ scale: 1.1 }}><Heart size={16} /><span className="text-xs font-medium">{formatNumber(tweet.likes)}</span></motion.div>
                            <motion.div className="flex items-center gap-2 text-gray-500 hover:text-emerald-500 transition-colors cursor-pointer" whileHover={{ scale: 1.1 }}><Repeat2 size={16} /><span className="text-xs font-medium">{formatNumber(tweet.retweets)}</span></motion.div>
                          </div>
                          <motion.div className="text-gray-500 group-hover:text-teal-400 transition-colors" whileHover={{ scale: 1.2, rotate: -10 }}><ExternalLink size={16} /></motion.div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.a>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        <motion.div className="text-center mt-14" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.3 }} viewport={{ once: true }}>
          <motion.a href="https://twitter.com/search?q=AI%20OR%20MachineLearning%20OR%20DataScience&f=live" target="_blank" rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/30 text-teal-400 hover:text-white hover:border-teal-400/50 transition-all duration-300 text-sm font-semibold"
            whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.95 }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            <span>Explore More on X</span><ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

// ============= CONTACT SECTION =============
const ContactSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setStatus({ type: "error", message: "Please fill in all required fields" });
      return;
    }
    setStatus({ type: "loading", message: "Sending message..." });
    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(formData) });
      const data = await response.json();
      if (data.success) {
        setStatus({ type: "success", message: "Message sent successfully!" });
        setFormData({ name: "", email: "", phone: "", interest: "", message: "" });
        setTimeout(() => { setIsModalOpen(false); setStatus({ type: "", message: "" }); }, 3000);
      } else throw new Error(data.error || "Failed to send");
    } catch (error) { setStatus({ type: "error", message: "Failed to send message. Please try again." }); }
  };

  const closeModal = () => { setIsModalOpen(false); setStatus({ type: "", message: "" }); };

  return (
    <section id="contact" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />
        <motion.div className="relative text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">Get In Touch</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Contact Us</h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg mb-10">Ready to start your AI journey? Get in touch with our team today</p>
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setIsModalOpen(true)}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 mx-auto">
            Start Your Journey <ArrowRight size={20} />
          </motion.button>
        </motion.div>

        {isModalOpen && (
          <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-white/10 rounded-3xl p-8 w-full max-w-lg relative max-h-[90vh] overflow-y-auto"
              initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}>
              <button onClick={closeModal} className="absolute top-4 right-6 text-3xl text-gray-400 hover:text-white transition">×</button>
              {status.type === "success" ? (
                <motion.div className="text-center py-12" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.5 }}><Check size={80} className="text-emerald-400 mx-auto mb-6" /></motion.div>
                  <h3 className="text-2xl font-bold text-white mb-3">Message Sent!</h3>
                  <p className="text-gray-400">{status.message}</p>
                </motion.div>
              ) : (
                <>
                  <div className="text-center mb-6 pb-4 border-b border-white/10">
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">Let's Connect</h3>
                    <p className="text-gray-400 text-sm mt-1">Fill out the form and we'll get back within 24 hours</p>
                  </div>
                  {status.type === "error" && (<motion.div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}><span className="text-red-400 text-xl">⚠</span><p className="text-red-400 text-sm">{status.message}</p></motion.div>)}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div><label className="block text-xs font-semibold text-teal-400 uppercase tracking-wider mb-2">Full Name *</label><input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe" className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition" /></div>
                    <div><label className="block text-xs font-semibold text-teal-400 uppercase tracking-wider mb-2">Email *</label><input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition" /></div>
                    <div><label className="block text-xs font-semibold text-teal-400 uppercase tracking-wider mb-2">Phone</label><input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 98765 43210" className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition" /></div>
                    <div><label className="block text-xs font-semibold text-teal-400 uppercase tracking-wider mb-2">Interested In</label><select name="interest" value={formData.interest} onChange={handleChange} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-teal-500 focus:outline-none transition"><option value="" className="bg-slate-900">Select an offering</option><option value="Training" className="bg-slate-900">Training</option><option value="Services" className="bg-slate-900">Services</option><option value="Product Development" className="bg-slate-900">Product Development</option><option value="Free Consultation" className="bg-slate-900">Free Consultation</option></select></div>
                    <div><label className="block text-xs font-semibold text-teal-400 uppercase tracking-wider mb-2">Message *</label><textarea rows="4" name="message" value={formData.message} onChange={handleChange} required placeholder="Tell us about your project..." className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none" /></div>
                    <button type="submit" disabled={status.type === "loading"} className="w-full py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold hover:shadow-lg hover:shadow-teal-500/40 transition disabled:opacity-70 flex items-center justify-center gap-2">
                      {status.type === "loading" ? (<><svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Sending...</>) : (<>Send Message</>)}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => (
  <footer className="relative border-t border-white/10 py-12 px-4">
    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div>
          <a href="#home" className="flex items-center gap-3 mb-4 group">
            <img src="/logo.png" alt="AI Factory Logo" className="h-10 w-10 object-contain rounded-lg" />
            <span className="text-white font-bold text-lg group-hover:text-teal-400 transition-colors">AI Factory</span>
          </a>
          <p className="text-gray-400 text-sm leading-relaxed">Empowering the next generation of AI professionals through world-class training and mentorship.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><a href="#home" className="hover:text-teal-400 transition">Home</a></li>
            <li><a href="#offerings" className="hover:text-teal-400 transition">Our Offerings</a></li>
            <li><a href="#about" className="hover:text-teal-400 transition">About Us</a></li>
            <li><a href="#ai-feed" className="hover:text-teal-400 transition">AI Pulse</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Our Offerings</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><a href="/training" className="hover:text-teal-400 transition">Training</a></li>
            <li><a href="/services" className="hover:text-teal-400 transition">Services</a></li>
            <li><a href="#contact" className="hover:text-teal-400 transition">Product Development</a></li>
            <li><a href="#contact" className="hover:text-teal-400 transition">Consultation</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Contact</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><a href="mailto:info@aifactory.com" className="hover:text-teal-400 transition">info@aifactory.com</a></li>
            <li><a href="tel:+919968284344" className="hover:text-teal-400 transition">+91 99682 84344</a></li>
            <li>Delhi, India</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-400 text-sm">© 2025 AI Factory. All rights reserved.</p>
        <div className="flex gap-6">
          <a href="https://x.com/AIFactoryco" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
          <a href="https://www.linkedin.com/company/ai-facrory/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
          <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
        </div>
      </div>
    </div>
  </footer>
);

// ============= MAIN HOME PAGE =============
export default function HomePage() {
  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar />
      <Hero />
      <OfferingsSection />
      <AboutSection />
      <AIFeedSection />
      <ContactSection />
      <Footer />
    </div>
  );
}